define(['lodash', 'prop-types', 'componentsCore', 'warmupUtilsLib', 'santa-components', 'svgShape'], function (_, PropTypes, componentsCore, warmupUtilsLib, santaComponents, svgShape) {
    'use strict';

    const consts = warmupUtilsLib.mediaConsts;

    const visiblePlayStates = {
        [consts.playbackTypes.IDLE]: true,
        [consts.playbackTypes.PAUSED]: true,
        [consts.playbackTypes.READY]: true,
        [consts.playbackTypes.STOPPING]: true,
        [consts.playbackTypes.PLAY_ENDED]: true
    };
    const fadeInParams = {
        duration: 0.5,
        to: {autoAlpha: 0.99}
    };
    const fadeOutParams = {
        duration: 0.3,
        to: {opacity: 0}
    };

    const hoverInParams = {
        duration: 0.25,
        scale: 1.07
    };
    const hoverOutParams = {
        duration: 0.15,
        scale: 1
    };

    function isInteractive(playbackState, canPause, isReplayDisabled) {
        if (isReplayDisabled && playbackState === consts.playbackTypes.PLAY_ENDED) {
            return false;
        }
        const isWaiting = !playbackState || playbackState === consts.playbackTypes.WAITING;
        const isPlayingAndCantPause = playbackState === consts.playbackTypes.PLAYING && !canPause;
        return !(isWaiting || isPlayingAndCantPause);
    }

    function isEditorMode(props) {
        return props.componentViewMode === 'editor';
    }

    /**
     * @class components.MediaOverlayControls (Big Play Button)
     * @extends {core.skinBasedComp}
     */

    return {
        displayName: 'MediaOverlayControls',
        mixins: [componentsCore.mixins.skinBasedComp, santaComponents.mixins.animationsMixin, componentsCore.mixins.createChildComponentMixin],

        propTypes: _.defaults({
            compProp: santaComponents.santaTypesDefinitions.Component.compProp,
            playbackState: santaComponents.santaTypesDefinitions.Media.playbackState,
            handleBehavior: santaComponents.santaTypesDefinitions.Behaviors.handleBehavior.isRequired,
            svgPropsMapForMediaControls: santaComponents.santaTypesDefinitions.VectorImage.svgPropsMapForMediaControls,
            controlsData: santaComponents.santaTypesDefinitions.Media.controlsData,
            isResponsive: santaComponents.santaTypesDefinitions.RendererModel.isResponsive,
            getIsPlaybackPlayIntent: santaComponents.santaTypesDefinitions.Media.getIsPlaybackPlayIntent,
            componentViewMode: santaComponents.santaTypesDefinitions.RenderFlags.componentViewMode,
            //injected prop from mediaPlayer
            interactionsNotificationRegistrar: PropTypes.func

        }, santaComponents.utils.santaTypesUtils.getSantaTypesByDefinition(svgShape.vectorImage)),

        componentDidMount() {
            this.props.interactionsNotificationRegistrar(this.handleUserInteraction);
        },

        getInitialState() {
            return {
                showReplay: false,
                showPlay: false,
                showPauseIndicator: false,
                showPlayIndicator: false
            };
        },

        handleUserInteraction(action) {
            if ('isHover' in action) {
                this.setState({playerInHoverState: action.isHover});
            }
        },

        setStateAndRelayout(state) {
            this.setState(state);
            this.registerReLayout();
        },

        componentWillReceiveProps(nextProps) {
            if (nextProps.playbackState !== undefined) {
                const {playbackState, controlsData} = nextProps;
                const isReplayDisabled = nextProps.controlsData.allowReplay === false;
                const hasIcon = this.hasIconData('playButton');
                if (isInteractive(
                    playbackState,
                    controlsData.canPause,
                    isReplayDisabled) && hasIcon) {
                    this.onMediaStateChanged(nextProps.playbackState);
                } else {
                    this.setState({
                        showPlay: false,
                        showReplay: false,
                        showPauseIndicator: false,
                        showPlayIndicator: false

                    });
                }
            }
        },

        componentDidUpdate(prevProps, prevState) {
            const shouldUpdateBigPlay = this.state.showPlay !== prevState.showPlay;
            const shouldAnimateHover = (this.state.showPlay || this.state.showReplay) &&
                this.state.playerInHoverState !== prevState.playerInHoverState;

            if (shouldAnimateHover) {
                const {scale, duration} = this.state.playerInHoverState ? hoverInParams : hoverOutParams;
                this.animate('indicator', 'BaseScale', duration, 0, {to: {scale}, ease: 'Sine.easeOut'});
            }
            if (shouldUpdateBigPlay) {
                const {to, duration} = this.state.showPlay ? fadeInParams : fadeOutParams;
                this.animate('indicator', 'BaseFade', duration, 0, {to, ease: 'Sine.easeIn'});
            }
        },

        /**
         * Change local state by playbackState
         * Optimized to change only when needed
         * @param {string} playbackState
         */
        onMediaStateChanged(playbackState) { // eslint-disable-line complexity
            const newState = {};
            const overrideVisibility = isEditorMode(this.props);

            if (overrideVisibility || this.shouldShowBigPlay(playbackState)) {
                newState.showPlay = true;
            } else if (this.shouldHideBigPlay(playbackState)) {
                newState.showPlay = false;
            }

            if (!_.isEmpty(newState)) {
                this.setStateAndRelayout(newState);
            }
        },

        shouldHideBigPlay(playbackState) {
            return this.state.showPlay && !visiblePlayStates[playbackState];
        },

        shouldShowBigPlay(playbackState) {
            const isHoverInteraction = this.props.controlsData.playerInHoverInteraction;
            const isDisabled = this.shouldDisableOnLastFrame(playbackState);
            return !this.state.showPlay &&
                !isHoverInteraction &&
                !isDisabled &&
                visiblePlayStates[playbackState] &&
                !this.props.getIsPlaybackPlayIntent();
        },

        shouldDisableOnLastFrame(playbackState) {
            const isReplayDisabled = this.props.controlsData.allowReplay === false;
            return isReplayDisabled && playbackState === consts.playbackTypes.PLAY_ENDED;
        },

        /**
         * Change visibility on click
         */
        clickAction() {
            const isPlaying = this.props.playbackState === consts.playbackTypes.PLAYING;
            const newState = {};

            if (this.hasIconData('playIndication') && this.hasIconData('pauseIndication') && !(this.state.showPlay || this.state.showReplay)) {
                newState.showPauseIndicator = isPlaying;
                newState.showPlayIndicator = !isPlaying;
            } else {
                newState.showPauseIndicator = false;
                newState.showPlayIndicator = false;
            }

            if (!_.isEmpty(newState)) {
                this.setStateAndRelayout(newState);
            }
        },

        /**
         * Get icon name by video state
         * @returns {*}
         */
        getIconSymbolName() {
            return 'playButton';
        },

        hasIconData(iconSymbolName) {
            return _.has(this.props.svgPropsMapForMediaControls, iconSymbolName);
        },

        /**
         * Get a vectorImage by current symbol name
         * @param {string} iconSymbolName
         * @returns {ReactCompositeComponent}
         */
        getIconComponent(iconSymbolName) {
            const iconProps = _.get(this.props.svgPropsMapForMediaControls, iconSymbolName);
            const props = _.assign({
                id: `${this.props.id}indicator`,
                key: iconSymbolName,
                ref: 'indicator',
                className: this.classSet({'indicator': true, 'hover': this.props.playerInHoverState})
            }, iconProps);
            return this.createChildComponent(null, 'wysiwyg.viewer.components.VectorImage', 'indicator', props);
        },

        getCatcherProperties() {
            const isReplayDisabled = this.props.controlsData.allowReplay === false;
            return _.assign(
                {
                    className: this.classSet({'interactive-player': isInteractive(this.props.playbackState, this.props.controlsData.canPause, isReplayDisabled)}),
                    onClick: this.clickAction
                },
                this.props.isResponsive && {
                    onClick: _.noop,
                    style: {pointerEvents: 'none'}
                }
            );
        },

        getSkinProperties() {
            const iconSymbolName = this.getIconSymbolName();
            const indicator = iconSymbolName ? this.getIconComponent(iconSymbolName) : null;
            const catcher = this.getCatcherProperties();
            return {
                '': {
                    'data-player-id': this.props.compProp.playerId,
                    style: this.props.style
                },
                indicator,
                catcher
            };
        }
    };
});
